/**
 * Brand configuration — single source of truth for every user-facing
 * "PokerBlitz" string in the app. P5.2 of the GameRuleset roadmap
 * (StackedStreak-82r).
 *
 * Why this exists: P5.1's decision doc commits to a build-flavors
 * strategy — one codebase, multiple App Store / Play Store entries
 * with different bundle ids, names, and branding. This module is
 * the runtime substrate. P5.3 wires the build pipeline to inject
 * VITE_VARIANT_KEY at compile time; the runtime hook below picks
 * the right brand from a registry. If no variant key is set, we
 * default to the PokerBlitz brand — that's the canonical first
 * variant and also the current production app.
 *
 * Storage choice — compile-time, not DB-backed (per P5.1 deferred
 * decision). Reasoning: branding is bound to bundle id + App Store
 * listing + marketing site, none of which can change at runtime
 * without a redeploy anyway. DB-backed branding would imply we
 * could hot-swap "PokerBlitz" → "Echidna" while users have the
 * app open, which would be a worse experience than just shipping
 * a separate variant build.
 *
 * Surface area kept minimal on purpose. Add fields here only when
 * a real consumer needs them; resist creating settings nobody
 * reads.
 */

export interface BrandConfig {
  /** Variant key — matches VITE_VARIANT_KEY at build time. */
  key: string;
  /** Full app name, e.g. "PokerBlitz". Shown in titles + headers. */
  appName: string;
  /** Short name for tight spaces, e.g. "PB". Falls back to appName. */
  appNameShort?: string;
  /** One-line tagline used in marketing surfaces. */
  tagline: string;
  /** Support / privacy contact email. */
  supportEmail: string;
  /** Public marketing site (no trailing slash). */
  marketingUrl: string;
  /** Default theme id from the ThemeContext registry. */
  primaryThemeId: string;
  /** Bundle / package id (informational here; the actual binary id
   *  is set by the build script via Info.plist / AndroidManifest). */
  bundleId: string;
  /** Apple App Store id, when known — used for app-store deep
   *  links from share sheets / install prompts. */
  appStoreId?: string;
  /** Google Play package, when known — same use case. */
  playStorePackage?: string;
}

/**
 * Canonical PokerBlitz brand. Also the fallback when no variant
 * key is provided at build time. Every field is non-nullable
 * specifically because new variants need to be EXPLICIT about
 * deviating — silent inheritance is how brand strings drift.
 */
export const DEFAULT_POKER_BLITZ_BRAND: BrandConfig = {
  key: 'poker_blitz',
  appName: 'PokerBlitz',
  appNameShort: 'PB',
  tagline: 'Fast-paced poker hand matching',
  supportEmail: 'admin@pokerblitzgame.com',
  marketingUrl: 'https://pokerblitzgame.com',
  primaryThemeId: 'classic',
  bundleId: 'com.pokerblitz.app',
  appStoreId: undefined, // TODO: fill in once App Store id is public
  playStorePackage: 'com.pokerblitz.app',
};

/**
 * Echidna brand — the second white-label title on the engine
 * (P5.4 second-game-kind, game_kind='word-match'). Shares the same
 * Supabase backend and engine; differentiation is rules-deep (a
 * WordMatchRule, not poker-hand detection) per the 4.3 constraint
 * in docs/VARIANT_STRATEGY.md. Reuses the 'classic' theme for now;
 * a Echidna-specific theme is a follow-up (see docs/WORDBLITZ.md).
 * Store ids are placeholders until the app is registered.
 */
export const ECHIDNA_BRAND: BrandConfig = {
  key: 'echidna',
  // ECHIDNA (user decision, 2026-07-28). Supersedes the round-7 "Monotreme"
  // pick, which itself superseded "Echidna"/"Echidna" — those were
  // both unfindable AND unownable.
  //
  // Round 7's note advised against Echidna because the name collides with
  // an existing word-game publisher; the user chose it anyway and has
  // already registered com.echidna.game on Google Play, so the decision is
  // made. Recorded here rather than re-litigated: if the App Store ever
  // rejects on name grounds, this comment is the reason why.
  //
  // The in-game rival that used to be called Echidna is gone (the par
  // display no longer names a character), so there is no internal clash.
  appName: 'Echidna',
  appNameShort: 'Echidna',
  tagline: 'The daily word hunt.',
  // NOT changed: these point at echidnagame.com, which is a domain that
  // actually exists. Inventing monotreme URLs would put dead links in the
  // store listing. Update once a domain is registered.
  supportEmail: 'admin@echidnagame.com',
  marketingUrl: 'https://echidnagame.com',
  primaryThemeId: 'classic',
  // com.echidna.game is now REGISTERED on Google Play, so this is fixed
  // for good — a package name cannot be changed after the listing exists.
  // The iOS App ID must be registered with the identical string in
  // Certificates, Identifiers & Profiles before the App Store Connect app
  // record can be created.
  bundleId: 'com.echidna.game',
  appStoreId: undefined, // TODO: fill in once App Store id is public
  playStorePackage: 'com.echidna.game',
};

/**
 * Variant registry. Each future game-kind ships its brand here
 * and selects via VITE_VARIANT_KEY at build time. Today the only
 * entry is poker_blitz; P5.4's PoC adds the second entry. Once a
 * variant ships to production, NEVER remove its entry — old
 * builds in users' hands still resolve through it.
 */
export const BRAND_REGISTRY: Record<string, BrandConfig> = {
  poker_blitz: DEFAULT_POKER_BLITZ_BRAND,
  echidna: ECHIDNA_BRAND,
};

/**
 * Resolve the active brand for this build. Reads VITE_VARIANT_KEY
 * (defined in vite.config.ts at build time). Falls back to
 * DEFAULT_POKER_BLITZ_BRAND when:
 *   - The env var is unset (e.g., npm run dev with no variant flag)
 *   - The env var points at an unknown variant key (mistyped at
 *     build time — log a warning so it's catchable in dev, but
 *     don't crash the app)
 */
let cachedBrand: BrandConfig | null = null;

export function getBrand(): BrandConfig {
  if (cachedBrand) return cachedBrand;

  const variantKey = (typeof import.meta !== 'undefined' &&
    (import.meta.env as { VITE_VARIANT_KEY?: string } | undefined)?.VITE_VARIANT_KEY) ||
    DEFAULT_POKER_BLITZ_BRAND.key;

  const found = BRAND_REGISTRY[variantKey];
  if (!found) {
    if (typeof console !== 'undefined') {
      console.warn(
        `[branding] unknown VITE_VARIANT_KEY "${variantKey}"; falling back to ${DEFAULT_POKER_BLITZ_BRAND.key}`,
      );
    }
    cachedBrand = DEFAULT_POKER_BLITZ_BRAND;
    return cachedBrand;
  }

  cachedBrand = found;
  return cachedBrand;
}

/**
 * Resolve a specific brand by key, independent of the active build's
 * VITE_VARIANT_KEY. Used by a sibling title's UI when it is hosted
 * inside another variant's build for play-testing — e.g. the Echidna
 * game mounted at /echidna inside the PokerBlitz build should still
 * title itself "Echidna", not inherit the host's brand. Falls back to
 * the default brand on an unknown key.
 */
export function getBrandByKey(key: string): BrandConfig {
  return BRAND_REGISTRY[key] ?? DEFAULT_POKER_BLITZ_BRAND;
}

/**
 * Test hook — wipes the cache so a fresh test can simulate a
 * different build's brand without rebuilding. Production code
 * should never call this.
 */
export function __resetBrandCacheForTests(): void {
  cachedBrand = null;
}
